import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Controller, useFormState } from 'react-hook-form';
import '../../styles/modal.css';
import axiosInstance from '../../api/axiosInstance';

const RECORDING_SELECT_MENU_STYLE = {
  margin: 0,
  padding: '0.25rem 0',
  listStyle: 'none',
  background: '#fff',
  border: '2px solid #d1d5db',
  borderRadius: '0.5rem',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.12)',
  overflowY: 'auto',
  maxHeight: '240px',
  zIndex: 12000,
};

const PRIORITY_OPTIONS = [
  { value: '', label: 'Select Priority' },
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' },
];

const RecordingFieldLabel = ({ htmlFor, error, children }) => (
  <label
    htmlFor={htmlFor}
    className={`block text-sm font-semibold recording-form-field-error ${
      error ? 'text-red-600' : 'text-gray-700'
    }`}
  >
    {error ? error.message : children}
  </label>
);

const RecordingModalSelect = ({
  id,
  value,
  onChange,
  onBlur,
  options,
  disabled = false,
  error = false,
  placeholder = 'Select...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const menuMaxHeight = 240;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < menuMaxHeight && rect.top > spaceBelow;

    setMenuPosition({
      position: 'fixed',
      top: openAbove ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      transform: openAbove ? 'translateY(-100%)' : 'none',
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);

    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const selectedOption = options.find(
    (option) => String(option.value) === String(value)
  );

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    onBlur?.();
    setIsOpen(false);
  };

  const triggerClassName = `w-full min-w-0 max-w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all bg-white ${
    error
      ? 'border-red-500 bg-red-50 focus:ring-red-500'
      : 'border-gray-300 bg-gray-50 hover:border-gray-400 focus:ring-green-500'
  }`;

  return (
    <>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((open) => !open)}
        className={triggerClassName}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
          textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer',
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flex: 1,
            color: selectedOption ? '#111827' : '#6b7280',
          }}
        >
          {selectedOption?.label || placeholder}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ flexShrink: 0, color: '#9ca3af' }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {isOpen &&
        menuPosition &&
        createPortal(
          <ul
            ref={menuRef}
            role="listbox"
            aria-labelledby={id}
            style={{ ...RECORDING_SELECT_MENU_STYLE, ...menuPosition }}
          >
            {options.map((option) => {
              const isSelected = String(value) === String(option.value);
              return (
                <li
                  key={option.value === '' ? '__empty__' : String(option.value)}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(option.value)}
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#f0fdf4' : 'transparent',
                    color: isSelected ? '#15803d' : '#111827',
                    fontWeight: isSelected ? 500 : 400,
                  }}
                >
                  {option.label}
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </>
  );
};

const normalizePriorityValue = (priority) => {
  if (!priority) return '';
  return String(priority).toLowerCase();
};

const ExtensionRecordingModal = ({
  isOpen,
  onClose,
  onRecordingStarted,
  onTestCaseUpdated,
  projectId,
  projectName,
  mode = 'create',
  testCaseId = null,
}) => {
  const isEditMode = mode === 'edit';
  const [isLoading, setIsLoading] = useState(false);
  const [isPrefilling, setIsPrefilling] = useState(false);
  const [prefillError, setPrefillError] = useState(null);
  const [environments, setEnvironments] = useState([]);
  const [environmentsLoading, setEnvironmentsLoading] = useState(false);
  const [stories, setStories] = useState([]);
  const [storiesLoading, setStoriesLoading] = useState(false);
  const [resolvedProjectId, setResolvedProjectId] = useState(projectId);

  const {
    control,
    handleSubmit,
    register,
    reset,
    watch,
    trigger,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      testCaseName: '',
      description: '',
      story: '',
      priority: '',
      environment: ''
    }
  });

  const { errors, isValid } = useFormState({ control });

  const fetchEnvironments = useCallback(async (envProjectId) => {
    if (!envProjectId) {
      setEnvironments([]);
      return;
    }

    try {
      setEnvironmentsLoading(true);
      const response = await axiosInstance.get(
        `/user_profile/list_environments?project_id=${envProjectId}`
      );

      if (response.data && response.status_code === 200) {
        setEnvironments(response.data || []);
      } else {
        console.error('Failed to fetch environments:', response);
        setEnvironments([]);
      }
    } catch (error) {
      console.error('Error fetching environments:', error);
      setEnvironments([]);
    } finally {
      setEnvironmentsLoading(false);
    }
  }, []);

  const fetchStories = useCallback(async (storyProjectId) => {
    if (!storyProjectId) {
      setStories([]);
      return;
    }

    try {
      setStoriesLoading(true);
      const response = await axiosInstance.get(
        `/jira_data/story_dropdown?project_id=${storyProjectId}`
      );

      if (response?.status === 'success' && Array.isArray(response?.stories)) {
        setStories(response.stories);
      } else {
        setStories([]);
      }
    } catch (error) {
      console.error('Error fetching project stories:', error);
      setStories([]);
    } finally {
      setStoriesLoading(false);
    }
  }, []);

  // Reset form when modal opens so stale validation state is cleared
  useEffect(() => {
    if (isOpen) {
      reset({
        testCaseName: '',
        description: '',
        story: '',
        priority: '',
        environment: ''
      });
      setPrefillError(null);
      setResolvedProjectId(projectId);
    }
  }, [isOpen, reset, projectId]);

  // Fetch environments when modal opens or projectId changes
  useEffect(() => {
    if (isOpen && projectId) {
      fetchEnvironments(projectId);
    }
  }, [isOpen, projectId, fetchEnvironments]);

  // Stories are only offered while recording a new test case
  useEffect(() => {
    if (isOpen && !isEditMode && projectId) {
      fetchStories(projectId);
    } else if (!isOpen) {
      setStories([]);
    }
  }, [isOpen, isEditMode, projectId, fetchStories]);

  // Prefill form details in edit mode
  useEffect(() => {
    if (!isOpen || !isEditMode || !testCaseId) return undefined;

    let cancelled = false;

    const fetchTestCaseDetails = async () => {
      try {
        setIsPrefilling(true);
        setPrefillError(null);

        const response = await axiosInstance.get(
          `/test_case/get_test_case_env?test_case_id=${testCaseId}`
        );
        const isSuccess =
          response?.status === 200 || response?.status_code === 200;
        const testCase = response?.test_case;

        if (!isSuccess || !testCase) {
          throw new Error('Failed to load test case details');
        }

        if (cancelled) return;

        const nextProjectId = testCase.project_id ?? projectId;
        setResolvedProjectId(nextProjectId);

        if (nextProjectId && nextProjectId !== projectId) {
          await fetchEnvironments(nextProjectId);
        }

        reset({
          testCaseName: testCase.name || '',
          description: testCase.description || '',
          story: '',
          priority: normalizePriorityValue(testCase.priority),
          environment: testCase.environment_id
            ? String(testCase.environment_id)
            : '',
        });
        await trigger();
      } catch (error) {
        console.error('Error loading test case details:', error);
        if (!cancelled) {
          setPrefillError('Failed to load test case details. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setIsPrefilling(false);
        }
      }
    };

    fetchTestCaseDetails();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isEditMode, testCaseId, projectId, reset, trigger, fetchEnvironments]);

  const selectedEnvironmentId = watch('environment');
  const selectedEnvironment = environments.find(env => env.id === parseInt(selectedEnvironmentId));

  const selectedStoryId = watch('story');

  const storyOptions = [
    {
      value: '',
      label: storiesLoading
        ? 'Loading stories...'
        : stories.length
          ? 'Select a story'
          : 'No stories available',
    },
    ...stories.map((story) => ({
      value: String(story.id),
      label: story.story_key,
    })),
  ];

  const environmentOptions = [
    {
      value: '',
      label: environmentsLoading ? 'Loading environments...' : 'Select an environment',
    },
    ...environments.map((env) => ({
      value: String(env.id),
      label: env.environment_name,
    })),
  ];

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      if (isEditMode) {
        const effectiveProjectId = resolvedProjectId || projectId;
        const response = await axiosInstance.put(
          `/test_case/update_test_case_env_details?test_case_id=${testCaseId}`,
          {
            project_id: effectiveProjectId,
            description: data.description?.trim() || '',
            priority: String(data.priority || '').toUpperCase(),
            environment_id: parseInt(data.environment, 10),
          }
        );

        const isSuccess =
          response?.status === 200 || response?.status_code === 200;

        if (!isSuccess) {
          throw new Error(response?.message || 'Failed to update test case');
        }

        onTestCaseUpdated?.(effectiveProjectId, {
          id: testCaseId,
          description: data.description?.trim() || '',
          priority: String(data.priority || '').toUpperCase(),
          environment_id: parseInt(data.environment, 10),
        });
        reset();
        onClose();
        return;
      }

      // Send message to browser extension
      window.postMessage(
        {
          source: "ROBITEST_CLOUD",
          action: "TRIGGER_START",
          sessionName: data.testCaseName.trim(),
          description: data.description.trim(),
          priority: data.priority,
          projectId: projectId,
          projectName: projectName,
          environmentId: selectedEnvironmentId,
          storyId: selectedStoryId ? parseInt(selectedStoryId, 10) : null,
          targetUrl: selectedEnvironment?.target_url || window.location.origin
        },
        "*"
      );

      onRecordingStarted?.(projectId);

      // Show success and close modal
      setTimeout(() => {
        setIsLoading(false);
        reset();
        onClose();
      }, 500);
    } catch (err) {
      console.error(
        isEditMode
          ? 'Error updating test case:'
          : 'Error triggering extension recording:',
        err
      );
      setIsLoading(false);
    } finally {
      if (isEditMode) {
        setIsLoading(false);
      }
    }
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  if (!isOpen) return null;

  const isFormBusy = isLoading || isPrefilling;
  const submitDisabled =
    isFormBusy || !isValid || environmentsLoading || Boolean(prefillError);

  return createPortal(
    <div data-rt-modal-overlay className="extension-recording-modal-overlay" onClick={handleCancel}>
      <div
        className="extension-recording-modal-panel"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2>{isEditMode ? 'Edit Test Case' : 'Record Test Case'}</h2>
            <p>
              {isEditMode
                ? 'Update test case details and environment'
                : 'Start recording your interactions with the extension'}
            </p>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4 max-h-96 overflow-y-auto">
          {/* Project Info */}
          {projectName && (
            <div className="recording-form-field p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-gray-700 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                <span className="font-medium">Project:</span>{' '}
                <span className="text-green-700">{projectName}</span>
              </p>
            </div>
          )}

          {prefillError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{prefillError}</p>
            </div>
          )}

          {/* Test Case Name Input */}
          <div className="recording-form-field space-y-2">
            <RecordingFieldLabel htmlFor="testCaseName" error={errors.testCaseName}>
              Test Case Name <span className="text-red-500">*</span>
            </RecordingFieldLabel>
            <input
              id="testCaseName"
              type="text"
              placeholder="e.g., Login Flow, User Registration"
              maxLength={51}
              {...register('testCaseName', {
                required: 'Test case name is required',
                minLength: {
                  value: 3,
                  message: 'Test case name must be at least 3 characters'
                },
                maxLength: {
                  value: 50,
                  message: 'Test case name must not exceed 50 characters'
                },
                validate: isEditMode
                  ? undefined
                  : {
                      allowedCharacters: (value) =>
                        /^[a-zA-Z0-9\s\-_]+$/.test(value) ||
                        'Only hyphen (-) and underscore (_) are allowed as special characters',
                      containsLetterOrNumber: (value) =>
                        /[a-zA-Z0-9]/.test(value) ||
                        'Test case name must include at least one letter or number'
                    },
              })}
              className={`w-full min-w-0 max-w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                errors.testCaseName
                  ? 'border-red-500 bg-red-50 focus:ring-red-500'
                  : isEditMode
                    ? 'border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed'
                    : 'border-gray-300 bg-gray-50 hover:border-gray-400 focus:ring-green-500'
              }`}
              disabled={isFormBusy || isEditMode}
              readOnly={isEditMode}
              autoFocus={!isEditMode}
            />
            {/* {errors.testCaseName && !isEditMode && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18.101 12.93a1 1 0 00-1.414-1.414L10 15.586l-6.687-6.687a1 1 0 00-1.414 1.414l8.1 8.1a1 1 0 001.414 0l8.1-8.1z" clipRule="evenodd" />
                </svg>
                {errors.testCaseName.message}
              </p>
            )} */}
            {isEditMode && (
              <p className="text-xs text-gray-500">Test case name cannot be changed while editing.</p>
            )}
          </div>

          {/* Description Input */}
          <div className="recording-form-field space-y-2">
            <RecordingFieldLabel htmlFor="description" error={errors.description}>
              Description
            </RecordingFieldLabel>
            <textarea
              id="description"
              placeholder="Add a description for this test case (optional)"
              maxLength={500}
              {...register('description', {
                maxLength: {
                  value: 500,
                  message: 'Description must not exceed 500 characters'
                }
              })}
              rows="3"
              className={`w-full min-w-0 max-w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all resize-none ${
                errors.description
                  ? 'border-red-500 bg-red-50 focus:ring-red-500'
                  : 'border-gray-300 bg-gray-50 hover:border-gray-400 focus:ring-green-500'
              }`}
              disabled={isLoading}
            />
            {errors.description && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18.101 12.93a1 1 0 00-1.414-1.414L10 15.586l-6.687-6.687a1 1 0 00-1.414 1.414l8.1 8.1a1 1 0 001.414 0l8.1-8.1z" clipRule="evenodd" />
                </svg>
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Story Dropdown — record mode only */}
          {!isEditMode && (
            <div className="recording-form-field space-y-2">
              <RecordingFieldLabel htmlFor="story" error={errors.story}>
                Story <span className="text-gray-400 font-normal">(optional)</span>
              </RecordingFieldLabel>
              <Controller
                name="story"
                control={control}
                render={({ field }) => (
                  <RecordingModalSelect
                    id="story"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    options={storyOptions}
                    placeholder={
                      storiesLoading ? 'Loading stories...' : 'Select a story'
                    }
                    disabled={isFormBusy || storiesLoading}
                    error={Boolean(errors.story)}
                  />
                )}
              />
            </div>
          )}

          {/* Priority Dropdown */}
          <div className="recording-form-field space-y-2">
            <RecordingFieldLabel htmlFor="priority" error={errors.priority}>
              Priority <span className="text-red-500">*</span>
            </RecordingFieldLabel>
            <Controller
              name="priority"
              control={control}
              rules={{
                validate: (value) =>
                  value !== '' && value != null ? true : 'Priority is required',
              }}
              render={({ field }) => (
                <RecordingModalSelect
                  id="priority"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  options={PRIORITY_OPTIONS}
                  placeholder="Select Priority"
                  disabled={isFormBusy}
                  error={Boolean(errors.priority)}
                />
              )}
            />
          </div>

          {/* Environment Dropdown */}
          <div className="recording-form-field space-y-2">
            <RecordingFieldLabel htmlFor="environment" error={errors.environment}>
              Environment <span className="text-red-500">*</span>
            </RecordingFieldLabel>
            <Controller
              name="environment"
              control={control}
              rules={{
                validate: (value) =>
                  value !== '' && value != null ? true : 'Environment is required',
              }}
              render={({ field }) => (
                <RecordingModalSelect
                  id="environment"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  options={environmentOptions}
                  placeholder={
                    environmentsLoading ? 'Loading environments...' : 'Select an environment'
                  }
                  disabled={isFormBusy || environmentsLoading}
                  error={Boolean(errors.environment)}
                />
              )}
            />
            {selectedEnvironment && (
              <p className="text-xs text-green-600 flex items-start gap-1 min-w-0">
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="min-w-0 break-all">URL: {selectedEnvironment.target_url}</span>
              </p>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="modal-footer">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            className="btn btn-outline"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={submitDisabled}
            className="btn btn-primary flex items-center gap-2"
          >
            {(isLoading || isPrefilling) && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isPrefilling
              ? 'Loading...'
              : isLoading
                ? isEditMode
                  ? 'Updating...'
                  : 'Starting...'
                : isEditMode
                  ? 'Update Test Case'
                  : 'Start Recording'}
          </button>
        </div>

        {/* Info Box — create/record mode only */}
        {!isEditMode && (
          <div className="bg-blue-50 border-t border-blue-200 px-6 py-3">
            <p className="text-xs text-blue-700 flex items-start gap-2">
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span>Make sure the extension is installed in your browser before starting the recording.</span>
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ExtensionRecordingModal;
